import * as Sentry from '@sentry/react'
import { Currency } from '@sequencehq/api'
import { downloadBlob } from '@sequencehq/utils'
import { useNotifications } from '@sequencehq/core-components'
import { useCallback } from 'react'
import { format } from '@sequencehq/utils/dates'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

type UseInvoicesGridCsvExport = () => {
  download: {
    available: boolean
    disabled: boolean
    onClick: (
      currency: Currency,
      startDate: Date | undefined,
      endDate: Date | undefined
    ) => void
  }
}

export const useInvoicesGridCsvExport: UseInvoicesGridCsvExport = () => {
  const { displayPromiseNotification } = useNotifications()

  function formatDate(
    date: Date | undefined,
    formatStr: string
  ): string | undefined {
    try {
      return date
        ? format(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
            formatStr
          )
        : undefined
    } catch (e) {
      return undefined
    }
  }

  const downloadCsv = useCallback(
    async ({
      currency,
      startDate,
      endDate
    }: {
      currency: Currency
      startDate: Date | undefined
      endDate: Date | undefined
    }) => {
      const downloadOperation = async () => {
        const invoicesCsvRes =
          await dashboard20240730Client.getInvoicesGridCsvDownload({
            invoiceCurrency: currency,
            startDate: formatDate(startDate, 'yyyy-MM-dd'),
            endDate: formatDate(endDate, 'yyyy-MM-dd')
          })

        if (!invoicesCsvRes.data) {
          throw new Error('An error occurred while downloading the invoices')
        }

        downloadBlob('invoice-totals-export.csv')(invoicesCsvRes.data)
        return invoicesCsvRes.data
      }

      await displayPromiseNotification(downloadOperation(), {
        loading: 'Preparing download',
        success: 'Download ready',
        error: 'CSV download failed'
      })
    },
    [displayPromiseNotification]
  )

  return {
    download: {
      available: true,
      disabled: false,
      onClick: (currency, startDate, endDate) =>
        void downloadCsv({ currency, startDate, endDate })
    }
  }
}
